<script setup lang="ts">
import { faUserTie } from '@fortawesome/pro-light-svg-icons';
import {
    faArrowRightFromBracket,
    faBadgeCheck,
    faBell,
    faBolt,
    faBriefcase,
    faBus,
    faCar,
    faChevronLeft,
    faEnvelope,
    faHotel,
    faPlaneDeparture,
    faTicket,
    faTrain,
    faUser,
    faUserGroup,
    faWallet,
    faRepeatAlt,
    faPen,
} from '@fortawesome/pro-regular-svg-icons';
import HeaderNavItem from './HeaderNavItem.vue';
import NavSubItem from './NavSubItem.vue';
import { phone24 } from '@static/customIcons';
import { UserType } from '@store/auth';
import useBillTracking from '@composable/useBillTracking';
import type { PaletteColorProp } from '#imports';

const { compact, color, disableSwitchAccount } = defineProps<{
    disableSwitchAccount?: boolean;
    mainAccountOnly?: boolean;
    compact?: boolean;
    color?: string | PaletteColorProp;
}>();
const { login, logout, switchAccount } = useAuth();
const router = useRouter();
const currentSearchForm = useCurrentSearchForm();
const authStore = useAuthStore();
const {
    userInfo,
    accountName,
    levelTitle,
    score,
    levelIndex,
    level,
    hasMultipleAccounts,
    userHasUnreadMessage,
    loggedIn,
} = classToRefs(authStore);
const layoutStore = useLayoutStore();
const {
    isExtraFeaturesEnabled,
    organizationalSupportNumber,
    isRedirectFromSafarmarket,
} = classToRefs(layoutStore);
const mounted = useMounted();
const { isMrbilit } = useWhiteLabel();
const onTracking = useBillTracking();

const userHovered = ref(false);

const privacy = computed(() => {
    if (mounted.value) {
        if (authStore.loggedIn) {
            return 'private';
        } else {
            return 'public';
        }
    }
    return undefined;
});
const userType = computed(() =>
    mounted.value ? authStore.currentUserType || UserType.User : UserType.User
);
const credit = computed(() => userInfo.value?.currentCredit || 0);
const creditSubtitle = computed(() => {
    const creditText = formatMoney(credit.value || 0, { preserveSign: true });
    if (credit.value > 0) {
        return creditText;
    }
    const days = userInfo.value?.lastStatusReportCheckoutDays || 0;
    return `${creditText}
    از آخرین صورت وضعیت تسویه نشده شما ${days} روز گذشته است.`;
});

const customSwitchAccount = async () => {
    if (disableSwitchAccount) {
        const { isConfirmed } = await swal({
            text: 'امکان تغییر حساب کاربری در فرایند خرید ممکن نیست.',
            icon: 'info',
            width: 600,
            showCancelButton: true,
            cancelButtonText: 'ماندن در صفحه',
            confirmButtonText: 'تغییر حساب و بازگشت به ابتدای فلوی خرید',
        });
        if (isConfirmed) {
            await switchAccount();
            const srpUrl = currentSearchForm.value.destination
                ? currentSearchForm.value.viewUrl
                : localStorage.getItem('viewUrl');
            if (srpUrl) {
                router.push(`${srpUrl}#force`);
            } else {
                router.push(`${currentSearchForm.value.serviceIndexUrl}#force`);
            }
        }
    } else {
        switchAccount();
    }
};
</script>

<template>
    <ul class="menu" :style="{ '--color': color }">
        <HeaderNavItem
            v-if="isExtraFeaturesEnabled && !compact"
            title="خدمات سفر"
            class="grow-after"
        >
            <NavSubItem
                title="بلیط هواپیما"
                position="header"
                :prepend-icon="faPlaneDeparture"
                to="/plane-ticket"
            />
            <NavSubItem
                title="رزرو هتل"
                position="header"
                :prepend-icon="faHotel"
                to="/hotel"
            />
            <NavSubItem
                title="بلیط قطار"
                position="header"
                :prepend-icon="faTrain"
                to="/train-ticket"
            />
            <NavSubItem
                title="بلیط اتوبوس"
                position="header"
                :prepend-icon="faBus"
                to="/bus-ticket"
            />
            <NavSubItem
                title="بلیط سواری"
                position="header"
                :prepend-icon="faCar"
                to="/taxi"
            />
        </HeaderNavItem>
        <HeaderNavItem
            v-if="
                isMrbilit &&
                (privacy === 'public' || userType === UserType.User)
            "
            title="راهکارهای سازمانی"
            :icon="faUserTie"
            :icon-default-color="color"
        >
            <NavSubItem title="فروش سازمانی" position="header" to="/b2b" />
            <NavSubItem title="سفرکارت" position="header" to="/b2b/gift-card" />
        </HeaderNavItem>
        <HeaderNavItem title="پشتیبانی ۲۴/۷" :icon="phone24">
            <NavSubItem
                v-if="userType === UserType.Organization"
                title="تماس با پشتیبانی"
                position="header"
                :href="`tel:+9821${organizationalSupportNumber}`"
            />
            <NavSubItem
                title="پرسش‌های متداول"
                position="header"
                to="/support/faq"
            />
            <NavSubItem
                title="پیشنهادها و شکایات"
                position="header"
                to="/support/feedback"
            />
            <NavSubItem
                title="شرایط و مقررات"
                position="header"
                to="/support/terms-and-conditions"
            />
        </HeaderNavItem>
        <HeaderNavItem
            v-if="userType !== UserType.User"
            title="گزارش‌های مالی و سفرها"
            :icon="faBriefcase"
            :icon-default-color="color"
        >
            <NavSubItem
                title="اعتبار کاربری"
                position="header"
                :subtitle="creditSubtitle"
                to="/panel/credit"
                class="credit"
                :append-icon="{
                    icon: faChevronLeft,
                    color: 'var(--yellow-700)',
                }"
            />
            <NavSubItem
                title="گزارش خریدها"
                position="header"
                to="/org/purchaseReport"
            />
            <NavSubItem
                title="دریافت صورت‌وضعیت"
                position="header"
                to="/org/statusReport"
            />
            <NavSubItem
                v-if="userInfo?.canDownloadHotelFactor !== false"
                title="دریافت فاکتور هتل"
                position="header"
                to="/org/hotelFactor"
            />
            <NavSubItem
                title="دریافت کلی فایل‌های بلیط"
                position="header"
                to="/org/downloadTickets"
            />
        </HeaderNavItem>
        <HeaderNavItem
            v-if="!compact"
            title="پیگیری خرید و کنسلی"
            :icon="faTicket"
            :icon-default-color="color"
            @click="onTracking"
        />
        <div
            v-show="loggedIn"
            class="user-management-container"
            :class="{ 'organizational-user': userType !== UserType.User }"
        >
            <div
                class="user-position"
                @mouseenter="() => (userHovered = true)"
                @mouseleave="() => (userHovered = false)"
            >
                <div class="user-management-position">
                    <HeaderNavItem
                        v-if="privacy === 'private'"
                        :title="accountName"
                        :icon="
                            userType !== UserType.User ? faBadgeCheck : faUser
                        "
                        :icon-default-color="color"
                        class="user-management"
                        handle-overflow
                        href="/panel"
                        top="40px"
                    >
                        <NavSubItem
                            v-if="hasMultipleAccounts"
                            title="تغییر حساب کاربری"
                            position="header"
                            class="switch-account"
                            :append-icon="faRepeatAlt"
                            @click="customSwitchAccount"
                        />
                        <NavSubItem
                            v-if="isExtraFeaturesEnabled"
                            title="مِستربلیط‌کلاب"
                            position="header"
                            to="/panel/club"
                            :subtitle="`سطح ${levelTitle} | ${score} امتیاز`"
                            :prepend-icon="`/img/club/level-${levelIndex + 1}.png`"
                            :append-icon="{
                                icon: faChevronLeft,
                                color: 'var(--cool-gray-800)',
                            }"
                            class="score"
                            :class="level"
                        />
                        <NavSubItem
                            v-if="userType === UserType.User"
                            title="اعتبار کاربری"
                            position="header"
                            to="/panel/credit"
                            :prepend-icon="{
                                icon: faWallet,
                                color: 'var(--cool-gray-800)',
                            }"
                        />
                        <NavSubItem
                            v-if="isExtraFeaturesEnabled"
                            title="رزرو خودکار"
                            position="header"
                            to="/panel/auto-reserve"
                            :prepend-icon="{
                                icon: faBolt,
                                color: 'var(--cool-gray-800)',
                            }"
                        />
                        <NavSubItem
                            v-if="isExtraFeaturesEnabled"
                            title="مدیریت رهگیری"
                            position="header"
                            to="/panel/trackings"
                            :prepend-icon="{
                                icon: faBell,
                                color: 'var(--cool-gray-800)',
                            }"
                        />
                        <NavSubItem
                            v-if="isExtraFeaturesEnabled"
                            title="مدیریت مسافران"
                            position="header"
                            to="/panel/pax"
                            :prepend-icon="{
                                icon: faUserGroup,
                                color: 'var(--cool-gray-800)',
                            }"
                        />
                        <NavSubItem
                            title="صندوق پیام"
                            position="header"
                            to="/panel/messages"
                            :prepend-icon="faEnvelope"
                            :class="{ unread: userHasUnreadMessage }"
                        />
                        <NavSubItem
                            title="خروج"
                            position="header"
                            class="logout"
                            :prepend-icon="faArrowRightFromBracket"
                            @click="logout"
                        />
                    </HeaderNavItem>
                </div>
                <Transition name="bounce">
                    <mr-icon
                        v-if="userHovered"
                        class="edit-user-icon"
                        :icon="faPen"
                        color="white"
                    />
                </Transition>
            </div>
        </div>
        <HeaderNavItem
            v-if="privacy === 'public' && !isRedirectFromSafarmarket"
            title="ورود به حساب کاربری"
            :icon="faUser"
            :icon-default-color="color"
            @click="
                login({
                    disableSwitchAccount: mainAccountOnly,
                })
            "
        />
    </ul>
</template>

<style lang="scss">
html.hide-logout {
    .logout {
        display: none !important;
    }
}
</style>

<style scoped lang="scss">
.menu {
    list-style: none none;
    margin: 0;
    width: calc(100% - 100px);
    user-select: none;
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    background: none;
    padding-right: 40px;
}

.bounce-enter-active {
    animation: bounce-in 0.5s;
}
.bounce-leave-active {
    animation: bounce-out 0.5s;
}
.bounce-move {
    transition: all 0.2s ease;
}
@keyframes bounce-in {
    0% {
        transform: translateX(50px);
        opacity: 0;
    }
    100% {
        transform: translateX(0px);
        opacity: 100;
    }
}
@keyframes bounce-out {
    0% {
        transform: translateX(20px);
        opacity: 100;
    }
    100% {
        transform: translateX(50px);
        opacity: 0;
    }
}
.user-management-container {
    width: 230px;
    border-radius: 20px;
    .user-position {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: space-between;
        transition-property: all;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 1s;
        padding-left: 12px;

        .user-management-position {
            position: relative;
            z-index: 99;
            .user-management {
                width: 230px;
                overflow: hidden;
                text-overflow: ellipsis;
                :deep(.sub-items) {
                    border-top-right-radius: 0;
                    border-top-left-radius: 0;
                    left: 0 !important;
                }
                .user-management-button {
                    :deep(.main) {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 12px !important;
                        .sub-title {
                            @include tg-m($w5);
                            color: usePalette($p-cool-gray, $p-700) !important;
                        }
                        .append {
                            width: 36px;
                            height: 36px;
                        }
                    }
                }
                :deep(.body) {
                    width: auto !important;
                    &:hover {
                        background-color: transparent;
                        .title {
                            overflow: hidden;
                            width: 155px;
                        }
                    }
                    .title {
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    .expand {
                        display: none;
                    }
                }
            }
        }
        .edit-user-icon {
            margin-right: -38px;
        }
    }
    &.organizational-user {
        background-color: usePalette($p-green) !important;
    }
    &:hover {
        background-color: usePalette($p-blue);
        border-radius: 8px 8px 0 0;
    }
}

.switch-account {
    border-top: 1px solid usePalette($p-cool-gray);
    :deep(.main) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px !important;
        .append {
            width: 36px;
            height: 36px;
        }
    }
    :deep(.sub-subtitle) {
        @include tg-s($color: usePalette());
        padding-inline: 12px;
    }
}
.logout {
    :deep(.sub-title),
    :deep(.mr-icon-svg) {
        color: usePalette($p-red) !important;
    }
}
.new {
    :deep(.sub-body) {
        display: flex;
        align-items: center;
        .sub-body-body {
            width: auto;
            padding-left: 4px;
        }
        .sub-subtitle {
            padding: 2px 4px 4px;
            background-color: usePalette($p-yellow, $p-200);
            border: 1px solid usePalette($p-yellow, $p-300);
            border-radius: 4px;
            @include tg-xs($color: usePalette($p-yellow, $p-700));
            line-height: 10px; // @warn uncontrolled property modify
        }
    }
}
.credit {
    padding: 10px 14px !important;
    :deep(.sub-body) {
        background-color: usePalette($p-yellow, $p-200) !important;
        border-radius: 8px;
        padding: 4px 8px;
        .main {
            padding: 0 !important;
        }
        .sub-title {
            @include tg-m($w7, usePalette($p-yellow, $p-700), true);
            flex-grow: 1;
        }
        .sub-subtitle {
            @include tg-s($color: usePalette($p-yellow, $p-700));
            white-space: pre-line;
        }
    }
}
.score {
    border-radius: 8px;
    margin-inline: 12px;
    padding: 4px 8px 4px 0 !important;
    margin-top: 4px;
    &.Level4 {
        background-color: usePalette($p-cyan, $p-100) !important;
    }
    &.Level3 {
        background-color: usePalette($p-yellow, $p-100) !important;
    }
    &.Level2 {
        background-color: usePalette($p-cool-gray, $p-100) !important;
    }
    &.Level1 {
        background-color: #ffede2 !important;
    }
    :deep(.sub-body) {
        .main {
            gap: 0;
            padding: 0 !important;
            display: flex;
            align-items: center;
            justify-content: space-between;
            .sub-title {
                @include tg-m($w5);
                flex-grow: 1;
            }
            .append {
                width: 36px;
                height: 36px;
            }
            .prepend {
                max-height: 28px;
                margin-left: 8px;
            }
        }
    }
    :deep(.sub-subtitle) {
        @include tg-s;
        padding: 0;
    }
}
.unread {
    position: relative;

    &::before {
        content: '';
        position: absolute;
        width: 6px;
        height: 6px;
        right: 12px;
        top: 16px;
        background-color: usePalette($p-yellow);
        border-radius: 100%;
    }
}
</style>
